import React, { useEffect, useState } from 'react';
import {
  Layout, Card, Button, Table, Modal, Form, Input, InputNumber, Select,
  Tag, Space, Typography, message, Row, Col, Tooltip, Popconfirm, Switch
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, AimOutlined, ArrowUpOutlined, ArrowDownOutlined,
  StarOutlined, SlidersOutlined, DatabaseOutlined, ApartmentOutlined
} from '@ant-design/icons';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Text } = Typography;
const { Option } = Select;

const MEASUREMENT_DIRECTION_CONFIG = {
  'Higher is Better': {
    color: '#52c41a',
    badgeColor: 'green',
    icon: <ArrowUpOutlined style={{ color: '#52c41a' }} />,
    label: 'Higher is Better',
    desc: 'Higher achievement yields better score (e.g. Sales, Revenue, Output)',
  },
  'Lower is Better': {
    color: '#fa8c16',
    badgeColor: 'orange',
    icon: <ArrowDownOutlined style={{ color: '#fa8c16' }} />,
    label: 'Lower is Better',
    desc: 'Lower value yields better score (e.g. Defects, Escalations, Cost)',
  },
  'Exact Target': {
    color: '#722ed1',
    badgeColor: 'purple',
    icon: <AimOutlined style={{ color: '#722ed1' }} />,
    label: 'Exact Target',
    desc: 'Matching target precisely is required (e.g. Headcount, On-time Launch)',
  },
  'Rating-Based': {
    color: '#1677ff',
    badgeColor: 'blue',
    icon: <StarOutlined style={{ color: '#1677ff' }} />,
    label: 'Rating-Based',
    desc: 'Evaluated by performance rating scale (1-5 Star / Level score)',
  },
};

export default function PmsKpiManagement() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [kras, setKras] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKpi, setEditingKpi] = useState(null);
  const [form] = Form.useForm();

  // Filters
  const [filterKraId, setFilterKraId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchKras = async () => {
    try {
      const res = await api.get('/admin/pms/kras');
      if (res.data?.success) {
        setKras(res.data.kras || []);
      }
    } catch (e) {
      console.error('Fetch KRAs error:', e);
    }
  };

  const fetchKpis = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/pms/kpis');
      if (res.data?.success) {
        setKpis(res.data.kpis || []);
      } else {
        message.error(res.data?.message || 'Failed to load KPIs');
      }
    } catch (e) {
      console.error('Fetch KPIs error:', e);
      message.error(e.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKras();
    fetchKpis();
  }, []);

  const handleOpenModal = (kpi = null, initialKraId = null) => {
    setEditingKpi(kpi);
    if (kpi) {
      const measurementVal = kpi.measurementMethod || kpi.performanceDirection || 'Higher is Better';
      form.setFieldsValue({
        kraTemplateId: kpi.kraTemplateId,
        kpiName: kpi.kpiName,
        description: kpi.description || '',
        target: kpi.target || '',
        minTarget: kpi.minTarget || '',
        maxTarget: kpi.maxTarget || '',
        unit: kpi.unit || '%',
        weightage: kpi.weightage !== undefined ? Number(kpi.weightage) : 0,
        measurementMethod: measurementVal,
        performanceDirection: measurementVal,
        dataSource: kpi.dataSource || 'Manual Input',
        frequency: kpi.frequency || 'Monthly',
        active: kpi.active !== undefined ? kpi.active : true,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        kraTemplateId: initialKraId || (kras.length > 0 ? kras[0].id : undefined),
        measurementMethod: 'Higher is Better',
        performanceDirection: 'Higher is Better',
        dataSource: 'Manual Input',
        frequency: 'Monthly',
        weightage: 25,
        target: '100',
        minTarget: '70',
        maxTarget: '120',
        unit: '%',
        active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingKpi(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const measurementVal = values.measurementMethod || values.performanceDirection || 'Higher is Better';
      const payload = {
        kraTemplateId: Number(values.kraTemplateId),
        kpiName: values.kpiName,
        description: values.description || null,
        target: values.target !== undefined && values.target !== null && String(values.target).trim() !== '' ? String(values.target).trim() : null,
        minTarget: values.minTarget !== undefined && values.minTarget !== null && String(values.minTarget).trim() !== '' ? String(values.minTarget).trim() : null,
        maxTarget: values.maxTarget !== undefined && values.maxTarget !== null && String(values.maxTarget).trim() !== '' ? String(values.maxTarget).trim() : null,
        unit: values.unit ? String(values.unit).trim() : '%',
        weightage: values.weightage !== undefined ? Number(values.weightage) : 0,
        measurementMethod: measurementVal,
        performanceDirection: measurementVal,
        dataSource: values.dataSource || 'Manual Input',
        frequency: values.frequency || 'Monthly',
        active: !!values.active,
      };

      if (editingKpi) {
        const res = await api.put(`/admin/pms/kpis/${editingKpi.id}`, payload);
        if (res.data?.success) {
          message.success('KPI Template updated successfully');
          handleCloseModal();
          fetchKpis();
        } else {
          message.error(res.data?.message || 'Failed to update KPI');
        }
      } else {
        const res = await api.post('/admin/pms/kpis', payload);
        if (res.data?.success) {
          message.success('KPI Template created successfully');
          handleCloseModal();
          fetchKpis();
        } else {
          message.error(res.data?.message || 'Failed to create KPI');
        }
      }
    } catch (e) {
      console.error('Save KPI error:', e);
      message.error(e.response?.data?.message || 'Failed to save KPI');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/admin/pms/kpis/${id}`);
      if (res.data?.success) {
        message.success('KPI Template deleted successfully');
        fetchKpis();
      } else {
        message.error(res.data?.message || 'Failed to delete KPI');
      }
    } catch (e) {
      console.error('Delete KPI error:', e);
      message.error(e.response?.data?.message || 'Failed to delete KPI');
    }
  };

  // Filtered KPIs
  const filteredKpis = kpis.filter(k => {
    const matchesKra = !filterKraId || k.kraTemplateId === filterKraId;
    const matchesSearch = !searchQuery ||
      k.kpiName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      k.kra?.kraName?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesKra && matchesSearch;
  });

  const columns = [
    {
      title: 'KPI Name & Description',
      key: 'kpiName',
      width: 260,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{record.kpiName}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Parent KRA',
      key: 'parentKra',
      width: 200,
      render: (_, record) => (
        <div>
          <Tag color="purple" style={{ borderRadius: '10px', fontWeight: '600', padding: '2px 8px' }}>
            {record.kra?.kraName || `KRA #${record.kraTemplateId}`}
          </Tag>
          {record.kra?.weightage !== undefined && (
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
              KRA Weightage: <strong>{record.kra.weightage}%</strong>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Target Criteria',
      key: 'targets',
      width: 220,
      render: (_, record) => (
        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#64748b', fontSize: '11px', minWidth: '55px' }}>Target:</span>
            <span style={{ fontWeight: '700', color: '#1677ff', fontSize: '13px' }}>
              {record.target || '-'} {record.unit || ''}
            </span>
          </div>
          {(record.minTarget || record.maxTarget) && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
              {record.minTarget && (
                <Tag color="orange" style={{ fontSize: '11px', borderRadius: '6px', padding: '0 6px', margin: 0 }}>
                  Min: <strong>{record.minTarget} {record.unit || ''}</strong>
                </Tag>
              )}
              {record.maxTarget && (
                <Tag color="purple" style={{ fontSize: '11px', borderRadius: '6px', padding: '0 6px', margin: 0 }}>
                  Cap: <strong>{record.maxTarget} {record.unit || ''}</strong>
                </Tag>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Measurement & Direction',
      key: 'methodFreq',
      width: 220,
      render: (_, record) => {
        const method = record.measurementMethod || record.performanceDirection || 'Higher is Better';
        const config = MEASUREMENT_DIRECTION_CONFIG[method] || {
          color: '#1677ff',
          badgeColor: 'default',
          icon: <AimOutlined />,
          label: method,
        };
        return (
          <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
            <div>
              <Tag
                color={config.badgeColor}
                icon={config.icon}
                style={{ fontWeight: '600', borderRadius: '10px', fontSize: '11px', padding: '2px 8px' }}
              >
                {config.label}
              </Tag>
            </div>
            <div style={{ color: '#64748b', fontSize: '11px', marginTop: '3px' }}>
              Source: <strong>{record.dataSource || 'Manual'}</strong>
            </div>
            <div>
              <Tag color="cyan" style={{ fontSize: '10px', marginTop: '2px', borderRadius: '6px' }}>
                {record.frequency || 'Monthly'}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Weightage',
      dataIndex: 'weightage',
      key: 'weightage',
      width: 110,
      render: (val) => (
        <Tag color="blue" style={{ fontSize: '12px', fontWeight: '700', borderRadius: '12px', padding: '2px 8px' }}>
          {Number(val || 0)}%
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'success' : 'error'} style={{ borderRadius: '10px', fontWeight: '600' }}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit KPI">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1677ff' }} />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete KPI Template?"
            description="Are you sure you want to delete this KPI template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete KPI">
              <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden', transition: 'margin-left 0.2s' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Performance Management - KPI Library" />
        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            
            {/* KPI Summary Header */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>TOTAL KPIs</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginTop: '4px' }}>{kpis.length}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>ACTIVE KPIs</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#52c41a', marginTop: '4px' }}>
                    {kpis.filter(k => k.active).length}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>TOTAL KRAs WITH KPIs</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1677ff', marginTop: '4px' }}>
                    {new Set(kpis.map(k => k.kraTemplateId)).size}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>PARENT KRAs</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#722ed1', marginTop: '4px' }}>
                    {kras.length}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Main Content Card */}
            <Card
              title={<span style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>KPI Templates Library</span>}
              extra={
                <Space wrap>
                  <Input.Search
                    placeholder="Search KPI or KRA..."
                    style={{ width: 200 }}
                    onSearch={(val) => setSearchQuery(val)}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                  />
                  <Select
                    placeholder="Filter by Parent KRA"
                    style={{ width: 220 }}
                    value={filterKraId}
                    onChange={(val) => setFilterKraId(val)}
                    allowClear
                  >
                    {kras.map((k) => (
                      <Option key={k.id} value={k.id}>
                        {k.kraName} ({k.weightage}%)
                      </Option>
                    ))}
                  </Select>
                  <Button icon={<ReloadOutlined />} onClick={() => { fetchKras(); fetchKpis(); }}>Refresh</Button>
                  <Button type="primary" icon={<PlusOutlined />} shape="round" onClick={() => handleOpenModal()}>
                    New KPI Template
                  </Button>
                </Space>
              }
              style={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}
            >
              <Table
                columns={columns}
                dataSource={filteredKpis}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </Card>

          </Space>
        </Content>
      </Layout>

      {/* Create / Edit Modal - Upgraded to 880px Spacious Modern Design */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: 4, height: 22, background: '#1677ff', borderRadius: 2 }} />
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              {editingKpi ? 'Edit KPI Template' : 'Create New KPI Template'}
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={880}
        destroyOnClose
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '16px' }}>
          
          {/* Card 1: KPI Identity & Parent KRA */}
          <Card
            type="inner"
            title={
              <Space>
                <ApartmentOutlined style={{ color: '#722ed1' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>General Information & Hierarchy</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={14}>
                <Form.Item
                  name="kraTemplateId"
                  label={<strong>Parent KRA</strong>}
                  rules={[{ required: true, message: 'Select Parent KRA' }]}
                >
                  <Select placeholder="Select Parent KRA (e.g. Sales Achievement - 40%)">
                    {kras.map((k) => (
                      <Option key={k.id} value={k.id}>
                        {k.kraName} — {k.weightage}% Weightage ({k.category || 'General'})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item
                  name="weightage"
                  label={<strong>KPI Weightage (%)</strong>}
                  rules={[{ required: true, message: 'Enter Weightage %' }]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    formatter={value => `${value}%`}
                    parser={value => value.replace('%', '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item label={<strong>Active Status</strong>}>
                  <div style={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                    <Form.Item name="active" valuePropName="checked" noStyle>
                      <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="kpiName"
                  label={<strong>KPI Name</strong>}
                  rules={[{ required: true, message: 'Enter KPI Name (e.g. Monthly Sales Revenue)' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="e.g. Monthly Sales Revenue, Customer Retention Rate, Code Quality Rating..." />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 2: Targets & Measurement Criteria */}
          <Card
            type="inner"
            title={
              <Space>
                <SlidersOutlined style={{ color: '#1677ff' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Target & Measurement Criteria</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="target"
                  label={
                    <Tooltip title="Base expected target for standard 100% appraisal score">
                      <strong>Target Value</strong> <span style={{ color: '#64748b', fontWeight: 'normal' }}>(Standard)</span>
                    </Tooltip>
                  }
                >
                  <Input placeholder="e.g. 100, 500000, 95" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="minTarget"
                  label={
                    <Tooltip title="Minimum acceptable target below which score is 0 or penalized">
                      <strong>Min Acceptable Target</strong> <span style={{ color: '#fa8c16', fontWeight: 'normal' }}>(Floor)</span>
                    </Tooltip>
                  }
                >
                  <Input placeholder="e.g. 70, 350000, 80" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="maxTarget"
                  label={
                    <Tooltip title="Maximum target cap beyond which overachievement is not extra credited">
                      <strong>Max Target (Cap)</strong> <span style={{ color: '#722ed1', fontWeight: 'normal' }}>(Ceiling)</span>
                    </Tooltip>
                  }
                >
                  <Input placeholder="e.g. 120, 600000, 100" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={14}>
                <Form.Item
                  name="measurementMethod"
                  label={<strong>Measurement & Performance Direction</strong>}
                  rules={[{ required: true, message: 'Select Measurement Method / Direction' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select placeholder="Select Performance Evaluation Rule">
                    <Option value="Higher is Better">
                      <Space>
                        <ArrowUpOutlined style={{ color: '#52c41a' }} />
                        <strong>Higher is Better</strong> (Higher achievement gives higher score)
                      </Space>
                    </Option>
                    <Option value="Lower is Better">
                      <Space>
                        <ArrowDownOutlined style={{ color: '#fa8c16' }} />
                        <strong>Lower is Better</strong> (Lower count gives higher score, e.g. Defects/Cost)
                      </Space>
                    </Option>
                    <Option value="Exact Target">
                      <Space>
                        <AimOutlined style={{ color: '#722ed1' }} />
                        <strong>Exact Target</strong> (Requires hitting target precisely)
                      </Space>
                    </Option>
                    <Option value="Rating-Based">
                      <Space>
                        <StarOutlined style={{ color: '#1677ff' }} />
                        <strong>Rating-Based</strong> (Evaluated on subjective 1-5 rating scale)
                      </Space>
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name="unit"
                  label={<strong>Unit of Measurement</strong>}
                  style={{ marginBottom: 0 }}
                >
                  <Select placeholder="e.g. %, INR, Count" allowClear showSearch>
                    <Option value="%">% (Percentage)</Option>
                    <Option value="INR">₹ (INR / Currency)</Option>
                    <Option value="Count">Count / Numbers</Option>
                    <Option value="Units">Units</Option>
                    <Option value="Days">Days</Option>
                    <Option value="Hours">Hours</Option>
                    <Option value="Score">Score (1-5)</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 3: Tracking & Evaluation Guide */}
          <Card
            type="inner"
            title={
              <Space>
                <DatabaseOutlined style={{ color: '#52c41a' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Tracking & Evaluation Guide</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="dataSource" label={<strong>Data Source</strong>}>
                  <Select>
                    <Option value="Manual Input">Manual Input (Self/Manager entered)</Option>
                    <Option value="System Generated">System Generated</Option>
                    <Option value="Sales CRM">Sales CRM Integration</Option>
                    <Option value="Attendance Log">Attendance Log</Option>
                    <Option value="ERP Systems">ERP Systems</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="frequency" label={<strong>Evaluation Frequency</strong>}>
                  <Select>
                    <Option value="Monthly">Monthly</Option>
                    <Option value="Quarterly">Quarterly</Option>
                    <Option value="Half-Yearly">Half-Yearly</Option>
                    <Option value="Annual">Annual</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label={<strong>Description / Evaluation Guide</strong>} style={{ marginBottom: 0 }}>
                  <Input.TextArea rows={2} placeholder="Explain how actual achievement will be recorded, verified, and scored against target thresholds..." />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <Button size="large" onClick={handleCloseModal}>Cancel</Button>
            <Button size="large" type="primary" htmlType="submit">
              {editingKpi ? 'Update KPI Template' : 'Create KPI Template'}
            </Button>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
}
